import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Gem, Sparkles, Star, MapPin, Phone, Clock, Scale, Wand2 } from "lucide-react";
import HeroCarousel from "@/components/HeroCarousel";
import CategoryBar from "@/components/CategoryBar";

const HERITAGE = "https://customer-assets.emergentagent.com/job_gem-catalogue/artifacts/1710wg27_IMG_4608.jpg";
const RINGSTUDIO_IMG = "https://customer-assets.emergentagent.com/job_gem-catalogue/artifacts/snmoynuf_IMG_5673.jpg";

export default function Landing() {
  return (
    <div className="relative">
      {/* HERO — image + restrained text overlay */}
      <HeroCarousel />

      {/* CATEGORY FILTER BAR */}
      <CategoryBar variant="landing" />

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-16">
          <div className="ornament mb-4">The Atelier</div>
          <h2 className="font-display text-4xl md:text-5xl text-ivory" style={{ fontWeight: 300 }}>A library of light.</h2>
          <p className="text-ivory/60 max-w-xl mx-auto mt-5">A considered selection, built over six decades of custom commissions. Every piece is finished by hand at our Sector 8-B showroom.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <CatCard title="Solitaires & Diamonds" sub="Certified, cut, and set by hand" href="/catalogue?has_diamond=true" icon={<Gem />} />
          <CatCard title="Gold, Rendered Honestly" sub="Hallmarked chains, bangles, karas" href="/catalogue?has_diamond=false" icon={<Sparkles />} />
          <CatCard title="Bridal & Heirloom" sub="Pendants, sets, mangalsutras" href="/catalogue?category=DIAMOND+PANDENT" icon={<Star />} />
        </div>
      </section>

      {/* HERITAGE */}
      <section className="relative py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-center">
          <div className="relative order-2 md:order-1">
            <div className="aspect-[4/5] overflow-hidden border border-rose-gold/20">
              <img src={HERITAGE} alt="Artisan at work" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-8 -right-8 hidden md:block bg-ink-800 border border-rose-gold/30 p-6 max-w-xs">
              <div className="font-display text-5xl text-rose-gold" style={{ fontWeight: 300 }}>1966</div>
              <div className="overline text-ivory/60 mt-1">A legacy of light</div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="ornament mb-4">The House</div>
            <h2 className="font-display text-4xl md:text-5xl text-ivory leading-tight" style={{ fontWeight: 300 }}>
              Since 1966. <em className="text-rose-gold" style={{ fontStyle: 'italic' }}>Built on Trust.</em><br/>
              Defined by Craft.
            </h2>
            <p className="mt-6 text-ivory/70 leading-relaxed">
              For over six decades, Bholaa Jewels has quietly served families who value authenticity
              over advertising and craftsmanship over trends.
            </p>
            <p className="mt-4 text-ivory/70 leading-relaxed">
              Established in 1966 in Sector 8-B, Chandigarh, by a family of generational
              jewellers, Bholaa Jewels began as one of the city's pioneering jewellery showrooms.
              Today, four generations later, it remains a family-led jewel house built not on
              promotion, but on reputation — earned through trust, discretion, and enduring
              relationships with generations of patrons.
            </p>
            <p className="mt-4 text-ivory/70 leading-relaxed">
              Every creation reflects our exclusive standards. Gold jewellery is hallmarked for
              assured purity, while every diamond is certified by respected institutions such as
              IGI and GIA. Our pricing is transparent and straightforward, based on the prevailing
              market gold rate of the day — free from seasonal markups, hidden charges, or
              unnecessary premiums.
            </p>
            <p className="mt-4 text-ivory/70 leading-relaxed">
              We do not create jewellery for a season. We create heirlooms — pieces designed to be
              worn every day with confidence, treasured for a lifetime, and passed on with pride
              to future generations.
            </p>
            <p className="mt-6 font-display text-lg leading-relaxed" style={{ color: '#D9BE6E', fontWeight: 500, fontStyle: 'italic' }}>
              Sixty years of trust. Four generations of excellence.<br/>
              One enduring commitment to timeless jewellery.
            </p>
            <Link to="/catalogue" className="inline-flex items-center gap-2 mt-8 text-rose-gold hover:text-champagne tracking-widest text-sm uppercase" data-testid="heritage-cta">
              Browse the Catalogue <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* RINGSTUDIO BANNER */}
      <section className="relative py-20 border-y border-rose-gold/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[5/4] overflow-hidden border border-rose-gold/30">
            <img src={RINGSTUDIO_IMG} alt="Ringstudio — every angle documented" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-ink-900/45 to-transparent" />
            <div className="absolute bottom-5 left-5 overline text-rose-gold bg-ink-900/70 px-3 py-1 border border-rose-gold/40 flex items-center gap-1"><Sparkles className="w-3 h-3" /> 138 pieces</div>
          </div>
          <div>
            <div className="ornament mb-4">✦ Ringstudio</div>
            <h2 className="font-display text-4xl md:text-5xl text-ivory leading-tight" style={{ fontWeight: 300 }}>
              138 rings.<br/><em className="text-rose-gold" style={{ fontStyle: 'italic' }}>Every angle documented.</em>
            </h2>
            <p className="mt-5 text-ivory/70 leading-relaxed">
              A dedicated collection of engagement, solitaire and heirloom rings. Each ring is
              photographed from six angles — front, side, top-down, close detail, on the hand, and
              in its presentation box — so you can inspect every facet of the piece before
              visiting the showroom.
            </p>
            <Link to="/ringstudio" className="inline-flex items-center gap-3 mt-8 bg-rose-gold text-ink-900 px-8 py-4 tracking-widest text-sm uppercase font-medium hover:bg-champagne" data-testid="ringstudio-cta">
              Explore Ringstudio <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* AI TOOLS SECTION */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 border-t border-rose-gold/10">
        <div className="text-center mb-14">
          <div className="ornament mb-4"><Sparkles className="w-3 h-3 inline mr-1" /> Powered by AI</div>
          <h2 className="font-display text-4xl md:text-5xl text-ivory" style={{ fontWeight: 300 }}>
            Old-world craft, <em className="text-rose-gold" style={{ fontStyle: 'italic' }}>new-world tools.</em>
          </h2>
          <p className="text-ivory/60 max-w-2xl mx-auto mt-5">
            Behind the bench, we use artificial intelligence to estimate weights from a photograph
            and to generate catalogue-ready creatives for every new piece. What used to take a day
            now takes a minute — so we can spend more time with the gold, and less with the admin.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          <AiCard
            icon={<Scale />}
            title="Weight Estimator"
            body="Upload any jewellery photograph and our AI estimates gold, diamond, and stone weight in seconds — then calculates the live price at today's rate. Useful for quick valuations on old pieces brought in by customers."
            badge="Claude Sonnet 4.5 Vision"
          />
          <AiCard
            icon={<Wand2 />}
            title="Creative Studio"
            body="Turns raw product photos into model shots, lifestyle flat-lays, and ready-to-share catalogue cards. Every new piece gets its own campaign imagery without a studio booking."
            badge="Gemini Nano Banana"
          />
        </div>
        <div className="text-center mt-12">
          <Link to="/ai-tools" className="inline-flex items-center gap-2 bg-rose-gold text-ink-900 px-8 py-4 tracking-widest text-sm uppercase font-medium hover:bg-champagne" data-testid="ai-tools-cta">
            Open AI Tools <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CONTACT / VISIT */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center mb-14">
          <div className="ornament mb-4">Visit</div>
          <h2 className="font-display text-4xl md:text-5xl text-ivory" style={{ fontWeight: 300 }}>Come by the showroom.</h2>
          <p className="text-ivory/60 max-w-xl mx-auto mt-5">Sit, take tea, try the pieces on. The catalogue is a window — the showroom is the room.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          <ContactCard icon={<MapPin />} label="Visit the showroom" lines={["SCF 33, Sector 8-B", "Chandigarh 160 009"]} />
          <ContactCard icon={<Phone />} label="Call or WhatsApp" lines={["+91 78148 54580", "We reply within the hour"]} href="https://wa.me/917814854580" linkLabel="Chat on WhatsApp →" />
          <ContactCard icon={<Clock />} label="Opening hours" lines={["Monday – Saturday", "10:30 AM – 8:00 PM"]} />
        </div>
      </section>
    </div>
  );
}

const CatCard = ({ title, sub, href, icon }) => (
  <a href={href} className="group relative overflow-hidden border border-white/10 hover:border-rose-gold/40 p-10 card-lift block" data-testid={`category-${title.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="text-rose-gold/60 mb-8 group-hover:text-rose-gold transition-colors">
      {React.cloneElement(icon, { className: "w-8 h-8", strokeWidth: 1 })}
    </div>
    <h3 className="font-display text-2xl text-ivory mb-2" style={{ fontWeight: 300 }}>{title}</h3>
    <p className="text-ivory/50 text-sm">{sub}</p>
    <div className="mt-6 overline text-rose-gold flex items-center gap-2">Discover <ArrowRight className="w-3 h-3" /></div>
  </a>
);


const ContactCard = ({ icon, label, lines, href, linkLabel }) => (
  <div className="glass p-8">
    <div className="w-10 h-10 flex items-center justify-center border border-rose-gold/40 text-rose-gold mb-4">
      {React.cloneElement(icon, { className: "w-4 h-4", strokeWidth: 1.5 })}
    </div>
    <div className="overline text-rose-gold mb-3">{label}</div>
    {lines.map((l) => (<div key={l} className="text-ivory/80">{l}</div>))}
    {href && (
      <a href={href} target="_blank" rel="noreferrer" className="mt-5 inline-flex text-rose-gold hover:text-champagne overline">{linkLabel}</a>
    )}
  </div>
);

const AiCard = ({ icon, title, body, badge }) => (
  <div className="glass p-8 flex gap-5">
    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center border border-rose-gold/40 text-rose-gold">
      {React.cloneElement(icon, { className: "w-5 h-5", strokeWidth: 1.5 })}
    </div>
    <div className="flex-1">
      <h3 className="font-display text-2xl text-ivory mb-2" style={{ fontWeight: 300 }}>{title}</h3>
      <p className="text-ivory/60 text-sm leading-relaxed">{body}</p>
      <div className="overline text-rose-gold/70 mt-4 text-[10px]">{badge}</div>
    </div>
  </div>
);

